import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuthOptional } from "@/contexts/AuthContext";
import { getCurrentAdminRoles } from "@/utils/adminRoles.functions";
import * as React from "react";

/**
 * يكشف أدوار الإدارة للمستخدم الحالي.
 *
 *  - isAdmin       : له صلاحيّات أدمن (admin أو super_admin)
 *  - isSuperAdmin  : super_admin فقط (الوحيد الذي يمنح/ينزع super_admin)
 *  - isStaffOnly   : staff بلا admin/super (وصول قراءة محدود)
 *  - hasAdminAccess: staff أو admin أو super_admin (للوصول للوحات الأدمن)
 *
 * cache 5 دقائق حتى لا نُعيد الاستعلام عند كلّ تنقّل.
 */
export function useIsAdmin(enabled = true) {
  const { user, loading: authLoading } = useAuthOptional();
  const fetchRoles = useServerFn(getCurrentAdminRoles);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["user-roles", user?.id ?? "anon"],
    enabled: enabled && !authLoading && !!user,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 5,
    retryDelay: (attempt) => Math.min(300 * 2 ** attempt, 2000),
    refetchOnMount: true,
    refetchOnReconnect: true,
    queryFn: async () => {
      if (!user) return { roles: [] as string[] };
      return fetchRoles();
    },
  });

  React.useEffect(() => {
    if (!enabled || authLoading || !user || !isError) return;
    const timer = window.setTimeout(() => {
      void refetch();
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [enabled, authLoading, user, isError, refetch]);

  const roles = data?.roles ?? [];
  const isSuperAdmin = roles.includes("super_admin");
  const isAdminRole = roles.includes("admin");
  const isStaff = roles.includes("staff");
  const shouldResolveRoles = enabled && !authLoading && !!user;
  const rolesResolved = !shouldResolveRoles || (!isLoading && !isError);

  return {
    isAdmin: isAdminRole || isSuperAdmin,
    isSuperAdmin,
    isStaffOnly: isStaff && !isAdminRole && !isSuperAdmin,
    hasAdminAccess: isAdminRole || isSuperAdmin || isStaff,
    roles,
    rolesResolved,
    loading: authLoading || (shouldResolveRoles && (!rolesResolved || isError)),
  };
}
